"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import {
  updateFestivalDetails,
  uploadFestivalImage,
  importImageFromUrl,
  searchVenues,
  type SearchVenuesResult,
} from "@/app/admin/actions";
import { Festival } from "@/lib/types";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useModalZIndex } from "@/lib/use-modal-z-index";
import { AppQueryProvider } from "@/app/providers";

interface FestivalAdminModalProps {
  festival: Festival;
  onClose: () => void;
}

export function FestivalAdminModal(props: FestivalAdminModalProps) {
  return <AppQueryProvider><FestivalAdminModalContent {...props} /></AppQueryProvider>;
}

function FestivalAdminModalContent({ festival, onClose }: FestivalAdminModalProps) {
  const zIndex = useModalZIndex();
  const [isPending, startTransition] = useTransition();
  const queryClient = useQueryClient();
  const router = useRouter();

  const [name, setName] = useState(festival.name);
  const [description, setDescription] = useState(festival.description ?? "");
  const [url, setUrl] = useState(festival.url ?? "");
  const [imageUrl, setImageUrl] = useState(festival.image_url ?? "");
  const [startDate, setStartDate] = useState(festival.start_date ?? "");
  const [endDate, setEndDate] = useState(festival.end_date ?? "");
  const [isUploading, setIsUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Venue search state
  const [selectedVenue, setSelectedVenue] = useState<SearchVenuesResult | null>(
    festival.venue
      ? {
          id: festival.venue.id,
          name: festival.venue.name,
          slug: festival.venue.slug,
          city: festival.venue.city,
          address: festival.venue.address,
        }
      : null
  );
  const [venueQuery, setVenueQuery] = useState("");
  const [venueResults, setVenueResults] = useState<SearchVenuesResult[]>([]);
  const [isSearchingVenues, setIsSearchingVenues] = useState(false);

  // Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Debounced venue search
  useEffect(() => {
    if (venueQuery.length < 2) { setVenueResults([]); return; }
    const timer = setTimeout(async () => {
      setIsSearchingVenues(true);
      try {
        const data = await searchVenues(venueQuery);
        setVenueResults(data);
      } catch { setVenueResults([]); }
      finally { setIsSearchingVenues(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [venueQuery]);

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("festivalId", String(festival.id));
      const publicUrl = await uploadFestivalImage(formData);
      setImageUrl(publicUrl);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImportUrl = async () => {
    const url = importUrl.trim();
    if (!url) return;
    setIsUploading(true);
    try {
      const publicUrl = await importImageFromUrl(url, "festivals");
      setImageUrl(publicUrl);
      setImportUrl("");
    } catch (err) {
      console.error("Import from URL failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = () => {
    startTransition(async () => {
      await updateFestivalDetails(festival.id, {
        name: name.trim() || festival.name,
        description: description.trim() || null,
        url: url.trim() || null,
        image_url: imageUrl.trim() || null,
        start_date: startDate.trim() || null,
        end_date: endDate.trim() || null,
        venue_id: selectedVenue?.id ?? null,
        city_slug: festival.city_slug,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      router.refresh();
      onClose();
    });
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md flex flex-col overflow-visible border border-neutral-800 bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -top-1 -left-1 h-3 w-3 border-t border-l border-acid" />
        <div className="absolute -top-1 -right-1 h-3 w-3 border-t border-r border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-neutral-800 p-5">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="min-w-0 flex-1 truncate bg-transparent font-mono text-sm font-bold tracking-wider text-white outline-none border-b border-transparent focus:border-acid/50"
          />
          <button
            onClick={onClose}
            className="shrink-0 text-neutral-500 transition-colors hover:text-white"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[70vh] p-5 space-y-5">
          {/* Image */}
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-neutral-500">
              Image
            </h3>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
                e.target.value = "";
              }}
            />
            <div className="group relative aspect-video w-full overflow-hidden rounded border border-neutral-700 bg-neutral-900">
              {imageUrl ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <span className="font-mono text-xs text-neutral-600">NO IMAGE</span>
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed"
              >
                <span className="font-mono text-[10px] font-bold text-white">
                  {isUploading ? "UPLOADING..." : "UPLOAD IMAGE"}
                </span>
              </button>
            </div>
            <div className="mt-1.5 flex gap-1">
              <input
                type="text"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="Paste image URL to import..."
                className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[10px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleImportUrl(); }
                }}
              />
              <button
                onClick={handleImportUrl}
                disabled={isUploading || !importUrl.trim()}
                className="shrink-0 rounded border border-neutral-700 px-2 py-1 font-mono text-[10px] font-bold text-neutral-400 transition-colors hover:text-white disabled:opacity-40"
              >
                {isUploading ? "..." : "IMPORT"}
              </button>
            </div>
          </div>

          {/* Details */}
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-neutral-500">
              Details
            </h3>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <label className="shrink-0 w-16 font-mono text-[10px] text-neutral-500">START</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 outline-none focus:border-acid/50 [color-scheme:dark]"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="shrink-0 w-16 font-mono text-[10px] text-neutral-500">END</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 outline-none focus:border-acid/50 [color-scheme:dark]"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="shrink-0 w-16 font-mono text-[10px] text-neutral-500">URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                />
              </div>
            </div>
          </div>

          {/* Venue */}
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-neutral-500">
              Venue
            </h3>
            {selectedVenue ? (
              <div className="flex items-center gap-2 rounded border border-neutral-700 bg-dark px-2 py-1">
                <span className="flex-1 truncate font-mono text-[11px] text-neutral-300">
                  {selectedVenue.name}
                  {selectedVenue.city && (
                    <span className="text-neutral-600"> / {selectedVenue.city}</span>
                  )}
                </span>
                <button
                  onClick={() => setSelectedVenue(null)}
                  className="shrink-0 text-neutral-500 hover:text-neutral-300"
                >
                  &times;
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={venueQuery}
                  onChange={(e) => setVenueQuery(e.target.value)}
                  placeholder="Search venue..."
                  className="w-full rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                />
                {isSearchingVenues && (
                  <p className="mt-1 font-mono text-[10px] text-neutral-600">searching...</p>
                )}
                {venueResults.length > 0 && (
                  <div className="mt-1 max-h-32 overflow-y-auto rounded border border-neutral-700 bg-dark">
                    {venueResults.map((v) => (
                      <button
                        key={v.id}
                        onClick={() => {
                          setSelectedVenue(v);
                          setVenueQuery("");
                          setVenueResults([]);
                        }}
                        className="flex w-full items-center gap-2 px-2 py-1.5 text-left transition-colors hover:bg-surface"
                      >
                        <span className="text-[11px] text-neutral-300">{v.name}</span>
                        {v.city && (
                          <span className="font-mono text-[10px] text-neutral-600">{v.city}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-neutral-500">
              Description
            </h3>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Festival description..."
              className="w-full rounded border border-neutral-700 bg-dark px-2 py-1.5 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t border-neutral-800 p-5">
          <button
            onClick={handleSave}
            disabled={isPending}
            className="flex-1 rounded bg-acid py-2.5 font-mono text-sm font-bold text-black transition-opacity cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isPending ? "SAVING..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}
